import { BaseChannelMessage, SetupMessageChannel } from './setup-message-channel'

export type ChannelReadyHandler = (port: MessagePort) => void
export type ChannelMessageHandler<T> = (data: T) => void

const debug = false
/*
I have seen the time between sending the init message and receiving the accepted message between 1 and 200ms.
The accepted time lag is a bit less than maxNrOfParallelChannels * channelReadyCheckInterval
On simulated slow 3G networks, the time between iframe loaded and setting up the channel is about 35 seconds.
*/
const channelReadyCheckInterval = 50
const maxChannelReadyCheckTime = 60 * 1000
const maxNrOfParallelChannels = 10

export class MessageChannelWrapper<T extends BaseChannelMessage> {
  private setupMessageChannels: SetupMessageChannel<T>[] = []
  channelReady = false
  channelInProgress = false
  private channelCounter = 0
  private iframe: HTMLIFrameElement | undefined
  private initMessage: T | undefined
  private channelReadyHandler: ChannelReadyHandler | undefined
  private handleReceivedChannelMessage: ChannelMessageHandler<T> | undefined
  private channelReadyCheckingStartTime = 0

  connect(
    iframe: HTMLIFrameElement,
    initMessage: any,
    channelReadyHandler: ChannelReadyHandler,
    handleReceivedChannelMessage: ChannelMessageHandler<T>,
  ) {
    // console.log(`Chaser: MessageChannelWrapper.connect`)
    if (!iframe) {
      throw new Error('iframe not found')
    }
    if (!iframe.contentWindow) {
      throw new Error('iframe content window not found')
    }
    if (!channelReadyHandler) {
      throw new Error('channel ready handler not found')
    }
    if (!handleReceivedChannelMessage) {
      throw new Error('message handler not found')
    }
    this.iframe = iframe
    this.initMessage = initMessage
    this.channelReadyHandler = channelReadyHandler
    this.handleReceivedChannelMessage = handleReceivedChannelMessage
    this.setupIFrameEventListeners()
    this.startChannelReadyChecker()
  }

  private setupIFrameEventListeners() {
    this.iframe!.addEventListener('load', () => {
      if (debug) {
        console.log('Chaser: iframe loaded', this.channelInProgress, this.channelReady)
      }
      if (this.channelReady) {
        this.restartChannelSetup()
      }
    })
  }

  private restartChannelSetup() {
    if (this.channelReady) {
      this.findActiveSetupMessageChannel()!.close()
    }
    this.resetState()
    this.startChannelReadyChecker()
  }

  private findActiveSetupMessageChannel(): SetupMessageChannel<T> | undefined {
    return this.setupMessageChannels.find((setupMessageChannel) => setupMessageChannel.channelReady)
  }

  private resetState() {
    this.channelReady = false
    this.channelInProgress = false
    this.channelReadyCheckingStartTime = 0
    this.setupMessageChannels.length = 0
  }

  private startChannelReadyChecker() {
    this.channelInProgress = true
    this.channelReadyCheckingStartTime = Date.now()
    const checker = () => {
      const channelReadyCheckingTime = Date.now() - this.channelReadyCheckingStartTime
      if (!this.channelReady && !!this.iframe?.contentWindow) {
        if (channelReadyCheckingTime <= maxChannelReadyCheckTime) {
          this.channelCounter++
          if (debug) {
            console.log(`Chaser: channel not ready, retrying...`, channelReadyCheckingTime, this.channelCounter)
          }
          if (this.setupMessageChannels.length >= maxNrOfParallelChannels) {
            this.setupMessageChannels.shift()!.close()
          }
          this.setupMessageChannels.push(this.setupMessageChannel())
          setTimeout(checker, channelReadyCheckInterval)
        } else {
          console.log(`Chaser: channel not ready, giving up...`, channelReadyCheckingTime, this.channelCounter)
        }
      }
      if (debug && !this.iframe?.contentWindow) {
        console.log(
          `Chaser: aborted channel setup, due to missing iframe.contentWindow. probably the browser is reloading`,
        )
      }
    }
    setTimeout(checker, channelReadyCheckInterval)
  }

  private setupMessageChannel() {
    const getReadySetupMessageChannel = (): number => {
      return this.setupMessageChannels.findIndex((setupMessageChannel) => setupMessageChannel.channelReady)
    }
    return new SetupMessageChannel<T>(
      this.channelCounter,
      this.iframe!,
      this.initMessage,
      (port) => {
        this.channelReady = true
        this.channelInProgress = false
        console.log(
          `Chaser: channel ${getReadySetupMessageChannel()} ready in ${
            Date.now() - this.channelReadyCheckingStartTime
          } ms`,
        )
        this.setupMessageChannels
          .filter((setupMessageChannel) => !setupMessageChannel.channelReady)
          .forEach((setupMessageChannel) => setupMessageChannel.close())
        this.channelReadyHandler!(port)
      },
      this.handleReceivedChannelMessage!,
    )
  }
}
